import { message } from 'antd'
import { useState } from 'react'

import ClearIcon from '@mui/icons-material/Clear'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { isNotUser } from '../../../../../components/Ability'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import UserSelectSearch from '../../../../../components/SelectSearch/UserSelectSearch'
import useAxios from '../../../../../hooks/useAxios'
import { useUser } from '../../../../../hooks/useUser'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import { IProduct } from '../../../../../types/product'
import { IUserLists } from '../../../../../types/user'
import { useDispatch, useSelector } from 'react-redux'
import { setProductSearchData } from '../../../../../../store/reducers/search/search.slice'
import store from 'store2'
import { LAST_PRODUCT_STORE } from '../../../../../constants/global'
import { uniqBy } from 'lodash'
import { isEmpty } from 'radash'
import { Tag } from '../../../../../components/Tag'
import { numberFormat } from '../../../../../utils'

interface OrderFormAddProductModalProps {
  onAdd: (value: OrderFormAddProductModalForm & { product: IProduct }) => void
  isOpen: boolean
  onClose: (visible: boolean) => void
}

export interface OrderFormAddProductModalForm {
  quantity: number
  discount: number
  total?: number
  size?: string
  commissionUsername?: string
  date?: string
  isFree?: string
}

const getAddProductFormValidationSchema = () => {
  return Yup.object({
    quantity: Yup.number().required('กรุณากรอกจำนวนสินค้า'),
    discount: Yup.number().required('กรุณากรอกส่วนลด'),
    // commissionUsername: Yup.string().required('กรุณาเลือกพนักงาน'),
  })
}

export const OrderFormAddProductModal = (
  props: OrderFormAddProductModalProps,
) => {
  const { onAdd, isOpen, onClose } = props

  const lastProductSelected = store.get(LAST_PRODUCT_STORE)

  const [lastSearchProduct, setLastSearchProduct] = useState<IProduct[] | null>(
    lastProductSelected,
  )

  const [searchProduct, setSearchProduct] = useState<string>('')
  const [product, setProduct] = useState<IProduct>()
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')
  const [searchUserCommission, setSearchUserCommission] = useState<string>('')
  const { user } = useUser()
  const enableCommission = isNotUser(user)
  const dispatch = useDispatch()
  const prevProduct = useSelector((state: any) => state.search.product)

  const formik = useFormik({
    initialValues: {
      quantity: 0,
      discount: 0,
      total: 0,
      commissionUsername: '',
      isFree: 'no',
      date: 'order',
    },
    validationSchema: getAddProductFormValidationSchema(),
    onSubmit: async (values: OrderFormAddProductModalForm) => {
      if (!product) {
        message.error('กรุณาเลือกสินค้า')
        return
      }

      if (enableCommission && !values.commissionUsername) {
        message.error('กรุณาเลือกพนักงาน เพื่อกำหนดค่าคอมมิชชั่น')
        return
      }

      if (Number(values?.total ?? 0) < 0) {
        message.error('ราคารวมต้องมากกว่า 0')
        return
      }

      if (
        Number(values?.discount ?? 0) >
        Number((product?.price ?? 0) * values?.quantity)
      ) {
        message.error(
          `ส่วนลดต้องน้อยกว่าราคาสินค้า: ${numberFormat(
            product?.price ?? 0,
          )} บาท`,
        )
        return
      }

      onAdd({
        product: product as IProduct,
        quantity: values.quantity,
        discount: values.discount,
        total: values.total,
        commissionUsername: values?.commissionUsername ?? '',
        isFree: values.isFree,
        date: values.date,
        size: values.size,
      })
      setResetAutoComplete(new Date().getTime().toString())
      formik.resetForm()
      setProduct(undefined)
      onClose(false)
    },
  })

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    //await mutate()
  }

  const userCommissionList = useAxios<PaginationResponseInterface<IUserLists>>({
    method: 'GET',
    url: '/users/merchant',
    params: {
      page: 1,
      perPage: 5,
      search: searchUserCommission,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchUserCommission = async (value: string) => {
    setSearchUserCommission(value)
    await userCommissionList.mutate()
  }

  const onSelectProduct = (product: IProduct) => {
    setProduct(product)
    dispatch(setProductSearchData({ product }))
    const discount = 0
    formik.setFieldValue('quantity', 1)
    formik.setFieldValue('discount', discount)
    formik.setFieldValue('size', '')
    formik.setFieldValue('total', product.price - discount)

    const products = store.get(LAST_PRODUCT_STORE) || []
    let lists = []
    if (!products || products.length < 10) {
      lists = [product, ...products]
    } else {
      lists = [product, ...products.slice(0, 9)]
    }
    lists = uniqBy(lists, '_id')
    store.set(LAST_PRODUCT_STORE, lists)
    setLastSearchProduct(lists)
    setSearchProduct?.(`${product.name} (${product.code})`)
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="lg">
      <DialogTitle>เพิ่มสินค้า</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            {!isEmpty(lastSearchProduct) && (
              <Grid item xs={12} lg={12}>
                <Typography fontSize={14} fontWeight={600} mb={0.5}>
                  สินค้าที่คุณเลือกล่าสุด 10 รายการ **(คลิกเพื่อเลือก)**
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {lastSearchProduct?.map((p) => (
                    <Tag
                      onClick={() => onSelectProduct(p)}
                      color={
                        p.objectId === product?.objectId ? 'error' : 'success'
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      {p.name} ({p.code})
                    </Tag>
                  ))}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} lg={12}>
              <ProductSelectSearch
                value={searchProduct ? searchProduct : ''}
                multiple={false}
                onSelect={(e) => e && onSelectProduct(e?.value!)}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                disabled={!!!product}
                id="quantity"
                fullWidth
                label="กรอกจำนวนสินค้า"
                name="quantity"
                helperText={
                  formik.touched.quantity && (formik.errors.quantity as string)
                }
                error={!!(formik.touched.quantity && formik.errors.quantity)}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const quantity = Number(e.target.value) || 0
                  formik.setFieldValue('quantity', quantity)
                  const total =
                    product?.price! * quantity - formik.values.discount
                  formik.setFieldValue('total', total)
                  const discount = Math.max(
                    0,
                    product?.price! * quantity - total,
                  )
                  formik.setFieldValue('discount', discount)
                }}
                value={formik.values.quantity}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                disabled={!!!product}
                id="discount"
                fullWidth
                label="กรอกส่วนลด"
                name="discount"
                helperText={
                  formik.touched.discount && (formik.errors.discount as string)
                }
                error={!!(formik.touched.discount && formik.errors.discount)}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const discount = Math.max(0, Number(e.target.value) || 0)
                  formik.setFieldValue('discount', discount)
                  const currentTotal = Number(formik.values.total)
                  const total = Math.max(
                    0,
                    product?.price! * formik.values.quantity - discount,
                  )
                  formik.setFieldValue('total', total)
                }}
                value={formik.values.discount}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                disabled={!!!product}
                id="size"
                fullWidth
                label="กรอกขนาดสินค้า"
                name="size"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.size}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                disabled={!!!product}
                id="total"
                fullWidth
                label="ราคารวม"
                name="total"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const total = e.target.value
                  formik.setFieldValue('total', total)
                  const discount = Math.max(
                    0,
                    product?.price! * formik.values.quantity - Number(total),
                  )
                  formik.setFieldValue('discount', discount)
                }}
                value={formik.values.total}
              />
            </Grid>
            {enableCommission && (
              <Grid item xs={12} lg={12}>
                {/* <SelectSearch<IUserLists>
                  resetAutoComplete={resetAutoComplete}
                  label={'ลงออเดอร์ (ค่าคอม) ให้พนักงาน'}
                  onSelect={(e) =>
                    formik.setFieldValue(
                      'commissionUsername',
                      e?.value.username,
                    )
                  }
                  search={searchUserCommission}
                  onSearch={handleSearchUserCommission}
                  name="commissionUsername"
                  helperText={
                    formik.touched.commissionUsername &&
                    (formik.errors.commissionUsername as string)
                  }
                  error={
                    !!(
                      formik.touched.commissionUsername &&
                      formik.errors.commissionUsername
                    )
                  }
                  onBlur={formik.handleBlur}
                  options={
                    userCommissionList.data?.records
                      .filter(
                        (userCommission: IUserLists) =>
                          userCommission.isAvailable,
                      )
                      .map((userCommission: IUserLists) => ({
                        label: userCommission.name,
                        image: userCommission.image,
                        value: userCommission,
                      })) || ([] as IOptionSelect<IUserLists>[])
                  }
                /> */}

                <UserSelectSearch
                  resetAutoComplete={resetAutoComplete}
                  label={'ลงออเดอร์ (ค่าคอม) ให้พนักงาน'}
                  onSelect={(e) =>
                    formik.setFieldValue(
                      'commissionUsername',
                      e?.value.username,
                    )
                  }
                  name="commissionUsername"
                  helperText={
                    formik.touched.commissionUsername &&
                    (formik.errors.commissionUsername as string)
                  }
                  error={
                    !!(
                      formik.touched.commissionUsername &&
                      formik.errors.commissionUsername
                    )
                  }
                  onBlur={formik.handleBlur}
                />
              </Grid>
            )}
            <Grid item xs={12} lg={6}>
              <TextField
                id="date"
                fullWidth
                label="วันที่สร้าง"
                name="date"
                select
                value={formik.values.date || 'order'}
                onChange={(e) => formik.setFieldValue('date', e.target.value)}
              >
                <MenuItem value="order">วันที่ของคำสั่งซื้อ</MenuItem>
                <MenuItem value="today">วันที่ปัจจุบัน</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="isFree"
                fullWidth
                label="สินค้าฟรี"
                name="isFree"
                select
                onChange={(e) => formik.setFieldValue('isFree', e.target.value)}
                value={formik.values.isFree || 'no'}
              >
                <MenuItem value="no">ไม่ใช่</MenuItem>
                <MenuItem value="yes">ใช่</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                disabled={formik.isSubmitting}
                startIcon={
                  formik.isSubmitting ? (
                    <CircularProgress size={18} />
                  ) : (
                    <SaveIcon />
                  )
                }
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                เพิ่มสินค้า
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
